<template>
    <div :class="[
        'form',
        'form__selectorgunits',
        menuActive ? 'form__selectorgunits--menuactive' : ''
    ]">
        <label
            v-if="label"
            :for="name | toKebabCase"
            :class="[
                'form__selectorgunits_title'
            ]"
        >{{label | toCapitalize}}<span class="asterisk" v-if="required">*</span></label>
        <div class="form__selectorgunits_wrapper">
            <input
                ref="input"
                type="text"
                :class="'form__selectorgunits_wrapper_input'"
                :value="valueString"
                :placeholder="placeholderOutput"
                :id="name | toKebabCase"
                :name="name | toKebabCase"
                :disabled="disabled"
                :data-vv-as="dataVvAs ? dataVvAs : name"
                v-validate="vValidate"
                @keydown="preventType"
                @focus="openMenu"
            >
            <component-button
                :class="'form__selectorgunits_wrapper_icon'"
                :name="'form-selectorgunits-wrapper-icon'"
                :icon="true"
                :icon-name="menuActive ? 'chevron-up' : 'chevron-down'"
                :icon-raw="true"
                :theme="''"
                @click="openMenu"
                v-on="menuActive ? { 'click': closeMenu } : { 'click': openMenu }"
            ></component-button>
        </div>
        <div
            v-show="menuActive"
            class="form__selectorgunits_menu"
        >
            <template v-show="items && items.length">
                <component-org-units-filter
                    v-if="keepAlive ? true : menuActive"
                    ref="orgunitstree"
                    :items="items"
                    :multiple="multiple"
                    :search-active="false"
                    :workspace-id="workspaceId"
                    :preselected="valueOutput"
                    :action-active="actionActive"
                    :allowed="allowed"
                    :check-parent-if-children-selected="checkParentIfChildrenSelected"
                    :prevent-initialize="preventInitialize"
                    :prevent-get-org-units="preventGetOrgUnits"
                    @emit-filter="handleEmitInput"
                    @emit-selected="handleEmitSelected"
                ></component-org-units-filter>
            </template>
        </div>
        <span
            class="form__error form__selectorgunits_error"
            v-if="errors.first(toKebabCase(name))"
        >{{ vValidateCustomError || errors.first(toKebabCase(name)) }}</span>
        <template v-if="$slots.default">
            <div class="form__selectorgunits_hint">
                <slot></slot>
            </div>
        </template>
    </div>
</template>

<script>
import { toKebabCase } from '@/functions'
import ComponentOrgUnitsFilter from '@/components/default/shared/ComponentOrgUnitsFilter'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentText from '@/components/default/shared/ComponentText'

export default {
  name: 'form-select-org-units',
  components: { ComponentText, ComponentButton, ComponentOrgUnitsFilter },
  data () {
    return {
      menuActive: false,
      selectedName: ''
    }
  },
  props: {
    items: {
      type: Array
    },
    allowed: {
      type: Array
    },
    multiple: {
      type: Boolean,
      default: true
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true,
      default: 'Label'
    },
    required: {
      type: Boolean,
      default: false
    },
    vValidate: {
      default: ''
    },
    vValidateCustomError: {
      type: String
    },
    dataVvAs: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    preventInitialize: {
      type: Boolean,
      default: false
    },
    preventGetOrgUnits: {
      type: Boolean,
      default: false
    },
    workspaceId: {
      type: String
    },
    value: {
      type: [Array, String, Object]
    },
    placeholder: {
      type: String,
      default: 'Select Organisation unit'
    },
    actionActive: {
      type: Boolean,
      default: true
    },
    checkParentIfChildrenSelected: {
      type: Boolean,
      default: true
    },
    keepAlive: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    valueString () {
      const value = this.value
      const valueType = value ? value.constructor : undefined
      let valueItemsLength
      let output

      if (valueType === String) {
        valueItemsLength = value ? 1 : 0
        output = value ? (this.selectedName || `${valueItemsLength} item${valueItemsLength !== 1 ? 's' : ''}`) : ''
      } else if (valueType === Array) {
        valueItemsLength = value.length
        if (valueItemsLength || !this.placeholder) {
          output = `${valueItemsLength} item${valueItemsLength !== 1 ? 's' : ''}`
        } else {
          output = this.placeholder
        }
      } else if (valueType === Object) {
        output = this.selectedName || value.name
      } else if (!value) {
        this.selectedName = ''
      }

      return output
    },
    valueOutput () {
      const value = this.value
      const valueType = value ? value.constructor : undefined
      let output

      if (valueType === String) {
        output = [value]
      } else if (valueType === Array) {
        output = value
      } else if (valueType === Object) {
        output = value.id
      }

      return output
    },
    placeholderOutput () {
      return this.selectedName || this.placeholder
    }
  },
  methods: {
    clearSelected () {
      if (this.$refs.orgunitstree) {
        this.$refs.orgunitstree.handleCheckedUnit()
      }
    },
    preventType (event) {
      event.preventDefault()
    },
    openMenu (event) {
      this.$validator.pause()
      if (event) {
        event.target.blur()
      }
      this.$validator.resume()
      this.menuActive = true
      if (!this.preventInitialize) {
        this.$nextTick(() => {
          if (this.$refs.orgunitstree) {
            this.$refs.orgunitstree.initializePreselectedValue()
          }
        })
      }
    },
    closeMenu () {
      this.menuActive = false
    },
    handleEmitInput (data) {
      this.$emit('input', data)
    },
    handleEmitSelected (data) {
      if (!data) return
      this.selectedName = data
    },
    autoCloseMenu (event) {
      const path = event.path || event.composedPath()

      if (!path) return
      let close = true
      for (let i = 0; i < path.length; i++) {
        if (!path[i].className) continue
        if (
          path[i].classList.contains('form__selectorgunits')
        ) {
          close = false
        }
      }
      if (close) {
        this.closeMenu()
      }
    },
    emitFocus () {
      const field = this.$validator.errors.items.find(obj => obj.field === toKebabCase(this.name))
      if (field) {
        this.$validator.reset(field)
      }
      this.$emit('emit-focus')
    }
  },
  mounted () {
    const overlay = document.getElementById('app')
    overlay.addEventListener('mousedown', this.autoCloseMenu)

    if (!this.preventInitialize) {
      this.$nextTick(() => {
        if (this.$refs.orgunitstree) {
          this.$refs.orgunitstree.initializePreselectedValue()
        }
      })
    }
  },
  beforeDestroy () {
    const overlay = document.getElementById('app')
    overlay.removeEventListener('mousedown', this.autoCloseMenu)
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/forms/formselectorgunits";
</style>
